import { useState, useMemo } from "react";
import FacetGroup from "../FacetGroup/FacetGroup.jsx";
import Button from "../../../Button/Button.jsx";
import CircleCheck from "../CircleCheck/CircleCheck.jsx";
import MagnifyingGlassIcon from "../../../Icon/MagnifyingGlassIcon.jsx";
import ContainerFull from "../../../../../../Common/js/components/Container/ContainerFull.jsx";
import cx from "classnames";
import {
  toggleItemInArray,
  sectionShape,
  facetGroupShape,
  pageShape,
} from "../../ReportBuilderTypes.js";
import * as R from "ramda";
import { arrayOf, func } from "prop-types";
import { set } from "js-cookie";

const propTypes = {
  sections: arrayOf(sectionShape).isRequired,
  facetGroups: arrayOf(facetGroupShape).isRequired,
  pages: arrayOf(pageShape).isRequired,
  onSaveStagedPages: func.isRequired,
  onCancelAddPages: func.isRequired,
};

const AddSections = (props) => {
  const {
    sections,
    facetGroups,
    pages,
    onSaveStagedPages,
    onCancelAddPages,
  } = props;

  const [stagedPages, setStagedPages] = useState(pages);

  const [query, setQuery] = useState("");
  const [activeFacets, setActiveFacets] = useState({
    sectionTopicGuid: [],
    sectionCountryGuid: [],
    sectionOtherMediaFacet: [],
  });

  const [filterOpen, setFilterOpen] = useState(false);

  const filteredSections = useMemo(() => {
    const sectionMatchesTopic = (section) => {
      return R.any((facet) => {
        return facet.includes(section["sectionTopicGuid"]);
      }, activeFacets["sectionTopicGuid"]);
    };

    const sectionMatchesCountry = (section) => {
      return R.any((facet) => {
        return facet.includes(section["sectionCountryGuid"]);
      }, activeFacets["sectionCountryGuid"]);
    };

    const sectionMatchesOtherMedia = (section) => {
      return R.any((facet) => {
        return facet.includes(section["sectionOtherMediaFacet"]);
      }, activeFacets["sectionOtherMediaFacet"]);
    }

    return R.compose(
      R.when(
        () => !R.isEmpty(activeFacets["sectionCountryGuid"]),
        R.filter((section) => sectionMatchesCountry(section)),
      ),
      R.when(
        () => !R.isEmpty(activeFacets["sectionTopicGuid"]),
        R.filter((section) => sectionMatchesTopic(section)),
      ),
      R.when(
        () => !R.isEmpty(activeFacets["sectionOtherMediaFacet"]),
        R.filter((section) => sectionMatchesOtherMedia(section)),
      ),
    )(sections);
  }, [activeFacets, sections]);

  const queryRegex = RegExp(query, "ig");

  const handleSearchSections = (e) => {
    setQuery(e.target.value);
  };

  const handleFacetChange = (facetGroupValue, facetGuid) => {
    setActiveFacets((prevState) => {
      return R.evolve({
        [facetGroupValue]: toggleItemInArray(facetGuid),
      })(prevState);
    });
  };

  const handleStageSection = (event, page) => {
    setStagedPages((prevState) => {
      return toggleItemInArray(page, [...prevState]);
    });
  };

  const handleStageAllPages = (section) => {
    const sectionPages = new Set(section.sectionPages);
    const intersection = stagedPages.filter(x => sectionPages.has(x));

    if (intersection.length === section.sectionPages.length) {
      // entire section had already been selected -> remove this section's pages
      const pagesNotInTheCurrentSection = stagedPages.filter(
        x => !sectionPages.has(x),
      );
      setStagedPages(pagesNotInTheCurrentSection);
    } else {
      // at least part of the section had not been selected -> add section's pages, without duplication
      const dedupedUnion = Array.from(
        new Set(stagedPages.concat(section.sectionPages)),
      );
      setStagedPages(dedupedUnion);
    }
  };

  return (
    <div className="add-sections">
      <ContainerFull>
        <div className="add-sections__header">
          <h2 className="add-sections__header-title">Add Narrative Sections</h2>
          <div className="add-sections__btn-container">
            <Button
              className="btn--primary-ghost add-sections__cancel-btn"
              onClick={() => {
                onCancelAddPages();
              }}
            >
              Cancel
            </Button>
            <Button
              className="btn--primary"
              onClick={() => onSaveStagedPages(stagedPages)}
            >
              Save
            </Button>
          </div>
        </div>
        <div className="add-sections__body">
          <div
            className={cx("add-sections__body-left", {
              "add-sections__body-left--active": filterOpen,
            })}
          >
            <div className="add-sections__filter-text">Filter:</div>
            {facetGroups.map((facetGroup, index) => (
              <FacetGroup
                key={index}
                title={facetGroup.facetGroupTitle}
                facets={facetGroup.facets}
                activeFacets={activeFacets[facetGroup.facetGroupValue]}
                onFacetChange={(facetGuid) =>
                  handleFacetChange(facetGroup.facetGroupValue, facetGuid)
                }
              />
            ))}
          </div>
          <div className="add-sections__body-right">
            <div className="add-sections__filter">
              <Button
                onClick={() => setFilterOpen(!filterOpen)}
                className="btn--primary"
              >
                Filter Results
              </Button>
            </div>
            <div className="sections">
              <div className="sections__header">
                <div className="sections__search">
                  <input
                    className="sections__search-input"
                    onChange={handleSearchSections}
                    placeholder="Search pages"
                  />
                  <MagnifyingGlassIcon
                    className="sections__search-icon"
                    width="16"
                    height="16"
                  />
                </div>
                <div className="sections__add-text">ADD TO REPORT</div>
              </div>
              <div className="sections">
                {filteredSections.map((section, index) => (
                  <div key={index} className="section">
                    {R.any(
                      (page) => R.test(queryRegex, page.pageTitle),
                      section.sectionPages,
                    ) && (
                      <div className="section__header">
                        <h2 className="section__title">
                          {section.sectionTopic}
                          {section.sectionCountry &&
                            ` in ${section.sectionCountry}`}
                        </h2>
                        <button
                          className="section__add-all"
                          type="button"
                          onClick={() => {
                            handleStageAllPages(section);
                          }}
                        >
                          add/remove all
                        </button>
                      </div>
                    )}

                    <ul className="section__subsections">
                      {section.sectionPages.map((page, index) => {
                        return (
                          queryRegex.test(page.pageTitle) && (
                            <li key={index}>
                              <div className="subsection">
                                <span className="subsection__title">
                                  {page.pageTitle}
                                </span>
                                <CircleCheck
                                  checked={R.includes(page, stagedPages)}
                                  onChange={(e) => handleStageSection(e, page)}
                                  type="checkbox"
                                />
                              </div>
                            </li>
                          )
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ContainerFull>
    </div>
  );
};

AddSections.propTypes = propTypes;
export default AddSections;
